/**
 * Dashboard Component
 * 
 * Main dashboard view displaying podcast statistics and activity overview.
 * Follows brand guidelines with consistent color usage and component styling.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { 
  Users, 
  Mail, 
  MapPin, 
  Calendar, 
  Globe 
} from 'lucide-react';
import { useAuthContext } from '@/features-flat/auth/useAuthContext';
import { guestService, nominationService, applicationService } from '@/services/api/database/supabase';
import { mapService } from '@/services/api/map';
import { GuestStatus, ApprovalStatus } from '@/types/models/schema';

/**
 * Dashboard statistics interface
 * 
 * Defines the structure of statistics displayed on the dashboard
 */
interface DashboardStats {
  /** Guest-related statistics */
  guests: {
    /** Total number of guests */
    total: number;
    /** Number of pending guests */
    pending: number;
    /** Number of confirmed guests */
    confirmed: number;
    /** Number of published guests */
    published: number;
    /** Number of recorded guests */
    recorded: number;
  };
  /** Nomination-related statistics */
  nominations: {
    /** Total number of nominations */
    total: number;
    /** Number of pending nominations */
    pending: number;
    /** Number of approved nominations */
    approved: number;
    /** Number of rejected nominations */
    rejected: number;
  };
  /** Application-related statistics */
  applications: {
    /** Total number of applications */
    total: number;
    /** Number of pending applications */
    pending: number;
    /** Number of approved applications */
    approved: number;
    /** Number of rejected applications */
    rejected: number;
  };
  /** Map-related statistics */
  map: {
    /** Total number of locations */
    totalLocations: number;
    /** Number of countries represented */
    countries: number;
    /** Number of guest locations */
    guestCount: number;
    /** Number of team locations */
    teamCount: number;
    /** Number of client locations */
    clientCount: number;
  };
}

/**
 * Dashboard Component
 * 
 * Main dashboard view displaying podcast statistics and activity overview.
 * 
 * @returns {JSX.Element} Rendered dashboard component
 */
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<DashboardStats>({
    guests: { total: 0, pending: 0, confirmed: 0, published: 0, recorded: 0 },
    nominations: { total: 0, pending: 0, approved: 0, rejected: 0 },
    applications: { total: 0, pending: 0, approved: 0, rejected: 0 },
    map: { totalLocations: 0, countries: 0, guestCount: 0, teamCount: 0, clientCount: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch guest stats
        const guestsResponse = await guestService.getAllGuests();
        if (guestsResponse.error) {
          throw guestsResponse.error;
        }
        
        const guests = guestsResponse.data || [];
        const guestStats = {
          total: guests.length,
          pending: guests.filter(g => g.status === 'pending').length,
          confirmed: guests.filter(g => g.status === 'confirmed').length,
          recorded: guests.filter(g => g.status === 'recorded').length,
          published: guests.filter(g => g.status === 'published').length
        };
        
        // Fetch nomination stats
        const nominationsResponse = await nominationService.getAllNominations();
        if (nominationsResponse.error) {
          throw nominationsResponse.error;
        }
        
        const nominations = nominationsResponse.data || [];
        const nominationStats = {
          total: nominations.length,
          pending: nominations.filter(n => n.status === 'pending').length,
          approved: nominations.filter(n => n.status === 'approved').length,
          rejected: nominations.filter(n => n.status === 'rejected').length
        };
        
        // Fetch application stats
        const applicationsResponse = await applicationService.getAllApplications();
        if (applicationsResponse.error) {
          throw applicationsResponse.error;
        }
        
        const applications = applicationsResponse.data || [];
        const applicationStats = {
          total: applications.length,
          pending: applications.filter(a => a.status === 'pending').length,
          approved: applications.filter(a => a.status === 'approved').length,
          rejected: applications.filter(a => a.status === 'rejected').length
        };
        
        // Fetch map stats
        const mapStatsResponse = await mapService.getLocationStatistics();
        if (mapStatsResponse.error) {
          throw mapStatsResponse.error;
        }
        
        const mapStats = mapStatsResponse.data || { 
          totalLocations: 0, 
          countries: 0,
          guestCount: 0,
          teamCount: 0,
          clientCount: 0
        };
        
        setStats({
          guests: guestStats,
          nominations: nominationStats,
          applications: applicationStats,
          map: mapStats
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setError(error instanceof Error ? error : new Error('Unknown error fetching dashboard stats'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 font-heading">
            Welcome back, {user?.firstName || 'Admin'}
          </h1>
          <p className="text-neutral-500 mt-1">
            Here's what's happening with the Recurring Value Podcast
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
          <Button 
            variant="default" 
            onClick={() => navigate('/guests/new')}
            icon={<Users size={16} />}
          >
            Add Guest
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/map')}
            icon={<Globe size={16} />}
          >
            View Map
          </Button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-md">
          <p className="font-medium">Error loading dashboard data</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}
      
      {/* Stats overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="transition-all duration-250 hover:shadow-soft-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary-600" />
              <span>Guests</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-neutral-900">
              {isLoading ? '...' : stats.guests.total}
            </div>
            <p className="text-neutral-500 text-sm mt-1">
              {isLoading ? '...' : stats.guests.confirmed} confirmed
            </p>
          </CardContent>
        </Card>
        
        <Card className="transition-all duration-250 hover:shadow-soft-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5 text-accent-500" />
              <span>Nominations</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-neutral-900">
              {isLoading ? '...' : stats.nominations.total}
            </div>
            <p className="text-neutral-500 text-sm mt-1">
              {isLoading ? '...' : stats.nominations.pending} pending review
            </p>
          </CardContent>
        </Card>
        
        <Card className="transition-all duration-250 hover:shadow-soft-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-success-500" />
              <span>Applications</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-neutral-900">
              {isLoading ? '...' : stats.applications.total}
            </div>
            <p className="text-neutral-500 text-sm mt-1">
              {isLoading ? '...' : stats.applications.pending} pending review
            </p>
          </CardContent>
        </Card>
        
        <Card className="transition-all duration-250 hover:shadow-soft-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-info-500" />
              <span>Locations</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-neutral-900">
              {isLoading ? '...' : stats.map.totalLocations}
            </div>
            <p className="text-neutral-500 text-sm mt-1">
              Across {isLoading ? '...' : stats.map.countries} countries
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs for detailed stats */}
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="guests">Guests</TabsTrigger>
          <TabsTrigger value="nominations">Nominations</TabsTrigger>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="map">Map</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                    <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                    <div className="h-4 bg-neutral-200 rounded w-5/6"></div>
                  </div>
                ) : (
                  <p>Recent activity will be displayed here</p>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Guests</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                    <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                    <div className="h-4 bg-neutral-200 rounded w-5/6"></div>
                  </div>
                ) : (
                  <p>Upcoming guests will be displayed here</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="guests">
          <Card>
            <CardHeader>
              <CardTitle>Guest Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                  <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-500">Total Guests</p>
                    <p className="text-xl font-bold">{stats.guests.total}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Pending</p>
                    <p className="text-xl font-bold">{stats.guests.pending}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Confirmed</p>
                    <p className="text-xl font-bold">{stats.guests.confirmed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Published</p>
                    <p className="text-xl font-bold">{stats.guests.published}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="nominations">
          <Card>
            <CardHeader>
              <CardTitle>Nomination Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                  <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-500">Total Nominations</p>
                    <p className="text-xl font-bold">{stats.nominations.total}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Pending</p>
                    <p className="text-xl font-bold">{stats.nominations.pending}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Approved</p>
                    <p className="text-xl font-bold">{stats.nominations.approved}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Rejected</p>
                    <p className="text-xl font-bold">{stats.nominations.rejected}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="applications">
          <Card>
            <CardHeader>
              <CardTitle>Application Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                  <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-500">Total Applications</p>
                    <p className="text-xl font-bold">{stats.applications.total}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Pending</p>
                    <p className="text-xl font-bold">{stats.applications.pending}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Approved</p>
                    <p className="text-xl font-bold">{stats.applications.approved}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Rejected</p>
                    <p className="text-xl font-bold">{stats.applications.rejected}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="map">
          <Card>
            <CardHeader>
              <CardTitle>Map Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
                  <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-500">Total Locations</p>
                    <p className="text-xl font-bold">{stats.map.totalLocations}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Countries</p>
                    <p className="text-xl font-bold">{stats.map.countries}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Guest Locations</p>
                    <p className="text-xl font-bold">{stats.map.guestCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Team Locations</p>
                    <p className="text-xl font-bold">{stats.map.teamCount}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
